import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { DashboardMetrics } from '../../hooks/useDashboard'
import { Card } from '../ui/Card'

interface TopRestaurantProps {
  restaurant: DashboardMetrics['top_restaurant']
  loading?: boolean
}

export const TopRestaurant: React.FC<TopRestaurantProps> = ({ restaurant, loading }) => {
  const navigate = useNavigate()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatTime = (minutes: number) => {
    return `${Math.round(minutes)}m`
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 rounded w-full"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </Card>
    )
  }

  if (!restaurant) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Restaurant</h3>
        <p className="text-gray-600 text-center py-8">No orders today yet</p>
      </Card>
    )
  }

  return (
    <Card className="p-6 bg-orange-50 border-orange-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Top Performing Restaurant</h3>
        <span className={`badge ${restaurant.is_active ? 'badge-success' : 'badge-error'}`}>
          {restaurant.is_active ? 'Open' : 'Closed'}
        </span>
      </div>

      <div className="mb-4">
        <h4 className="text-xl font-semibold text-gray-900 mb-1">{restaurant.name}</h4>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Orders Today</p>
          <p className="text-xl font-semibold text-gray-900">{restaurant.orders_today}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Revenue Today</p>
          <p className="text-xl font-semibold text-green-600">{formatCurrency(restaurant.revenue_today)}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Avg Order Value</p>
          <p className="text-lg font-semibold text-gray-900">{formatCurrency(restaurant.avg_order_value)}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Avg Fulfillment</p>
          <p className="text-lg font-semibold text-gray-900">{formatTime(restaurant.avg_fulfillment_time)}</p>
        </div>
      </div>

      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Cancellation Rate</span>
          <span className={`text-sm font-semibold ${
            restaurant.cancellation_rate > 20 ? 'text-red-600' :
            restaurant.cancellation_rate > 10 ? 'text-yellow-600' :
            'text-green-600'
          }`}>
            {restaurant.cancellation_rate.toFixed(1)}%
          </span>
        </div>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${
              restaurant.cancellation_rate > 20 ? 'bg-red-500' :
              restaurant.cancellation_rate > 10 ? 'bg-yellow-500' :
              'bg-green-500'
            }`}
            style={{ width: `${Math.min(restaurant.cancellation_rate, 100)}%` }}
          ></div>
        </div>
      </div>
    </Card>
  )
}
